import { AdministrationHelper } from "@/Administration/Helpers/AdministrationHelper";
import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { RemoveButton } from "@/Common/Components/Buttons/RemoveButton";
import { AddressControl, AddressControlDTO } from "@/Common/Components/Contacts/AddressControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { SiteCreateState } from "../SiteCreate";

interface IAddressSectionProps {
    addresses: Address[];
    gst: number;
    qst: number;
    hst: number;
    mutateState: (mutation: (state: SiteCreateState) => void, then?: () => void) => void;
}

function AddressSection(props: IAddressSectionProps): JSX.Element {
    const {
        addresses,
        gst,
        qst,
        hst,
        mutateState
    } = props;
    const rowStyle: React.CSSProperties = { marginBottom: "15px" };

    function onAddAddress(): void {
        mutateState(s => {
            s.addresses = addresses;
            s.addresses.push(Address.newAddress(ContactPointType.Work));
        });
    }

    function onAddressRemove(id: string, addressIndex: number): void {
        mutateState(
            s => {
                const addressToRemove = s.addresses.splice(addressIndex, 1);
                s.addressesToRemove.push(addressToRemove[0].ContactPoint_id);

                s.addresses = addresses.filter(p => p.Id !== id);

                if (s.addresses.length === 0) {
                    s.addresses.push(Address.newAddress(ContactPointType.Work));
                }

                const defaultTaxRates = AdministrationHelper.getTaxesForDefaultAddress(s.addresses);
                s.gst = defaultTaxRates?.GST ?? s.gst;
                s.qst = defaultTaxRates?.PST ?? s.qst;
                s.hst = defaultTaxRates?.HST ?? s.hst;
                s.genericTax = 0;
            }
        );
    }

    function onCivicAddressSelect(addressControlDto: AddressControlDTO, idx: number): void {
        mutateState(s => {
            s.addresses = addresses;
            let address = addresses[idx];
            const preferredAddressId = Address.getDefaultAddress(addresses).Id;
            const previousSelectedState = address.StateLookupId;

            address.StreetAddress = addressControlDto.civicAddressEdit;
            address.City = addressControlDto.cityEdit;
            address.ZipCode = addressControlDto.postalCodeEdit;
            address.StateLookupId = addressControlDto.selectedRegionLookupId;
            address.CountryLookupId = addressControlDto.selectedCountryLookupId;
            address.IsValid = addressControlDto.isPostalCodeValid;

            address.AvailableCountryRegionLookupIds = cacheStore.GetCountryRegionLookups(cacheStore.GetLookupById(addressControlDto.selectedCountryLookupId));

            s.addresses[idx] = address;

            if (address.Id === preferredAddressId && previousSelectedState !== address.StateLookupId) {
                const defaultTaxRates = AdministrationHelper.getTaxesForDefaultAddress(s.addresses);
                s.gst = defaultTaxRates?.GST ?? gst;
                s.qst = defaultTaxRates?.PST ?? qst;
                s.hst = defaultTaxRates?.HST ?? hst;
                s.genericTax = 0;
            }
        });
    }

    function onAddressTypeChange(idx: number, typeLookupId: string): void {
        mutateState(s => {
            s.addresses = addresses;
            let address = addresses[idx];

            address.ContactPointTypeLookupId = typeLookupId;

            s.addresses[idx] = address;
        });
    }

    function setPreferredAddress(id: string): void {
        mutateState(
            s => {
                s.addresses.forEach(a => a.IsPreferred = a.Id === id);

                const defaultTaxRates = AdministrationHelper.getTaxesForDefaultAddress(s.addresses);
                s.gst = defaultTaxRates?.GST ?? s.gst;
                s.qst = defaultTaxRates?.PST ?? s.qst;
                s.hst = defaultTaxRates?.HST ?? s.hst;
                s.genericTax = 0;
            }
        );
    }

    return (
        <div>
            <div className="view-section-header">
                <h3 className="view-section-title">{cacheStore.GetString("AddressLabel")}</h3>
                <div className="text-right push-right-25">
                    <a onClick={() => onAddAddress()}>{cacheStore.GetString("AddLabel")}</a>
                </div>
            </div>
            {addresses.map((x, idx) => (
                <div className="view-section-body" key={"__a" + x.Id}>
                    <div className="row" style={{ marginBottom: "15px" }}>
                        <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                            <label className="form-label">{cacheStore.GetString("TypeLabel")}</label>
                        </div>
                        <div className="col-lg-5 col-md-9 col-sm-12 ">
                            <SelectLookupControl
                                IncludeEmptyChoice={false}
                                Value={x.ContactPointTypeLookupId}
                                AvailableValues={x.AvailableContactPointTypeLookupIds.map(x => x.ToComboboxItem())}
                                Callback={type => onAddressTypeChange(idx, type)}
                                Item={x}
                            />
                        </div>
                        <RemoveButton onRemove={() => onAddressRemove(x.Id, idx)} />
                    </div>
                    <AddressControl
                        id={x.Id}
                        initialStreetAddress={x.StreetAddress}
                        initialCity={x.City}
                        initialPostalCode={x.ZipCode}
                        initialCountryLookupId={x.CountryLookupId}
                        initialStateLookupId={x.StateLookupId}
                        onNotifyParentCallback={dto => onCivicAddressSelect(dto, idx)}
                        rowClassName="row"
                        labelClassName="col-lg-4 col-md-3 col-sm-12 vcenter text-right"
                        bodyClassName="col-lg-5 col-md-9 col-sm-12 "
                        rowStyle={rowStyle}
                        needValidation={true}
                        alwaysShowCityAndZipCode={true}
                    />
                    <div className="row" style={{ marginBottom: "15px" }}>
                        <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right" />
                        <div className="col-lg-5 col-md-9 col-sm-12">
                            <CheckboxControl
                                checked={x.IsPreferred}
                                label={cacheStore.GetString("PreferredAddressDLabel")}
                                onChange={() => setPreferredAddress(x.Id)}
                                item={x}
                            />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default AddressSection;
